import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Wifi, Copy, CheckCircle2, XCircle, Clock, Zap, MapPin, User, ArrowRight, Ticket, RefreshCw, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtIDR = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');
const fmtDur = (period, unit) => {
  if (!period) return '-';
  const map = { hours: 'Jam', days: 'Hari', weeks: 'Minggu', months: 'Bulan' };
  return `${period} ${map[unit] || unit || ''}`;
};

export default function HotspotRedeem() {
  const [sp] = useSearchParams();
  const code = (sp.get('u') || sp.get('code') || '').trim().toUpperCase();
  const pw = (sp.get('p') || sp.get('password') || '').trim();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedUser, setCopiedUser] = useState(false);
  const [copiedPass, setCopiedPass] = useState(false);

  useEffect(() => {
    if (!code) { setError('Kode voucher tidak ditemukan di URL'); setLoading(false); return; }
    setLoading(true);
    axios.get(`${API_URL}/api/public/hotspot/voucher`, { params: { u: code, p: pw || undefined } })
      .then(r => setData(r.data.data))
      .catch(e => setError(e.response?.data?.detail || 'Voucher tidak ditemukan'))
      .finally(() => setLoading(false));
  }, [code, pw]);

  const copy = (val, type) => {
    navigator.clipboard.writeText(val);
    if (type === 'user') { setCopiedUser(true); setTimeout(() => setCopiedUser(false), 2000); }
    else { setCopiedPass(true); setTimeout(() => setCopiedPass(false), 2000); }
  };

  const copyAll = () => {
    if (!data) return;
    navigator.clipboard.writeText(`Username: ${data.code}\nPassword: ${data.password}`);
    setCopiedUser(true); setCopiedPass(true);
    setTimeout(() => { setCopiedUser(false); setCopiedPass(false); }, 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 dark:from-slate-900 dark:via-slate-900 dark:to-orange-950 py-6 px-3 sm:py-10">
      <div className="max-w-md mx-auto space-y-4">
        {/* Brand */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 bg-white dark:bg-slate-800 rounded-full px-4 py-2 shadow-md">
            <Wifi className="w-5 h-5 text-amber-500" />
            <span className="font-bold text-slate-900 dark:text-white text-sm">Kumara Hotspot</span>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-16">
            <RefreshCw className="w-10 h-10 animate-spin mx-auto text-amber-500 mb-3" />
            <p className="text-slate-500">Memuat info voucher...</p>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 text-center">
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-1">Voucher Tidak Valid</h2>
            <p className="text-sm text-slate-500">{error}</p>
            <p className="text-xs text-slate-400 mt-4">Kode: <span className="font-mono">{code || '-'}</span></p>
          </div>
        )}

        {/* Content */}
        {!loading && data && (
          <>
            {/* Status Warning if used/expired */}
            {data.status_msg && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-700 dark:text-red-300 text-sm">Voucher Tidak Bisa Dipakai</p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">{data.status_msg}</p>
                </div>
              </div>
            )}

            {/* Package Card */}
            <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl shadow-xl p-5 text-white relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold">
                    {data.status === 'available' ? '✓ Voucher Aktif' : data.status}
                  </span>
                  <Ticket className="w-6 h-6 opacity-70" />
                </div>
                <h1 className="text-2xl font-bold">{data.package_name}</h1>
                <p className="text-white/90 text-sm mt-1">{data.branch_name}</p>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg py-2 border border-white/20">
                    <Clock className="w-4 h-4 mx-auto mb-1 opacity-80" />
                    <p className="text-[10px] opacity-80">Masa Aktif</p>
                    <p className="font-bold text-sm">{fmtDur(data.validity_days, data.validity_unit)}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg py-2 border border-white/20">
                    <Zap className="w-4 h-4 mx-auto mb-1 opacity-80" />
                    <p className="text-[10px] opacity-80">Kecepatan</p>
                    <p className="font-bold text-sm">{data.speed_down || '-'}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg py-2 border border-white/20">
                    <p className="text-[10px] opacity-80 mt-3">Harga</p>
                    <p className="font-bold text-sm">{fmtIDR(data.price)}</p>
                  </div>
                </div>
              </div>
              <Wifi className="absolute -right-4 -bottom-4 w-32 h-32 opacity-10 pointer-events-none" />
            </div>

            {/* Credentials Card */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <User className="w-4 h-4 text-amber-500" /> Login Anda
                </h3>
                <button onClick={copyAll} className="text-xs bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg font-medium flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Salin Semua
                </button>
              </div>

              <div className="space-y-2">
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">Username</label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 font-mono font-bold text-slate-900 dark:text-white break-all">
                      {data.code}
                    </div>
                    <button onClick={() => copy(data.code, 'user')} className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600" data-testid="copy-user-btn">
                      {copiedUser ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-slate-500" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">Password</label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 font-mono font-bold text-slate-900 dark:text-white break-all">
                      {data.password}
                    </div>
                    <button onClick={() => copy(data.password, 'pass')} className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600" data-testid="copy-pass-btn">
                      {copiedPass ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-slate-500" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* How To */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-4">
              <h3 className="font-bold text-blue-900 dark:text-blue-300 flex items-center gap-2 mb-2">
                <ArrowRight className="w-4 h-4" /> Cara Pakai
              </h3>
              <ol className="text-xs text-blue-800 dark:text-blue-300 space-y-2 list-decimal ml-4">
                <li>Connect ke <b>WiFi Hotspot Kumara</b> di lokasi <b>{data.branch_name}</b></li>
                <li>Buka browser — otomatis akan muncul halaman login</li>
                <li>Tekan <b>Salin</b> lalu <b>Paste</b> username dan password di atas</li>
                <li>Klik <b>Login</b> di halaman hotspot → selamat menikmati internet! 🎉</li>
              </ol>
            </div>

            {/* Agent Info */}
            {data.agent_name && (
              <div className="text-center text-xs text-slate-500 dark:text-slate-400 flex items-center justify-center gap-1">
                <MapPin className="w-3 h-3" /> Dijual oleh: <span className="font-semibold text-slate-700 dark:text-slate-300">{data.agent_name}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
